import { INITIAL_SCALE, PARALLAX_SPEED } from '../constants/parallax';
import { getThemeSettings } from '../utils';

/**
 * @type {import('./index.d').parallaxEffect}
 */
export const parallaxEffect = (context) => {
  const collectionImage =
    getThemeSettings(context)?.additionalElements?.collectionHeader?.elements?.collectionImage;
  const { parallaxEffect: isParallaxEnabled, directionParallax } = collectionImage || {};

  if (!isParallaxEnabled) {
    return;
  }

  const collectionImg = document.querySelector('.boost-sd__header-image-inner');

  if (!collectionImg) {
    console.log('Collection image element is not found for parallax effect.');
    return;
  }

  const parentContainer = collectionImg.parentElement;
  if (!parentContainer) {
    console.log('Parent container not found.');
    return;
  }

  // Setup parent container styles
  const containerStyles = {
    position: 'relative',
    overflow: 'hidden',
  };

  // Setup image styles
  const imageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    willChange: 'transform',
    transform: `scale(${INITIAL_SCALE})`,
    transformOrigin: 'top center',
    transition: 'none',
  };

  Object.assign(parentContainer.style, containerStyles);
  Object.assign(collectionImg.style, imageStyles);

  const updateParallax = () => {
    const rect = parentContainer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const elementTop = rect.top;
    const elementHeight = rect.height;

    // Only apply parallax when element is in view
    if (elementTop > viewportHeight || elementTop + elementHeight < 0) {
      return;
    }

    // Calculate the relative position of the element in the viewport
    const relativePos = elementTop / viewportHeight;

    // Calculate parallax offset
    const offset = relativePos * PARALLAX_SPEED * elementHeight;

    // Apply transform based on direction
    const translateValue =
      directionParallax === 'horizontal'
        ? `translate3d(-${offset}px, 0, 0) scale(${INITIAL_SCALE})`
        : `translate3d(0, -${offset}px, 0) scale(${INITIAL_SCALE})`;

    collectionImg.style.transform = translateValue;
  };

  // Throttle scroll handler for performance
  let ticking = false;
  const scrollHandler = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateParallax();
        ticking = false;
      });
      ticking = true;
    }
  };

  // Handle window resize
  const resizeHandler = () => {
    Object.assign(parentContainer.style, containerStyles);
    updateParallax();
  };

  // Initial update
  updateParallax();

  // Event listeners
  window.addEventListener('scroll', scrollHandler, { passive: true });
  window.addEventListener('resize', resizeHandler, { passive: true });

  // Cleanup function
  return () => {
    window.removeEventListener('scroll', scrollHandler);
    window.removeEventListener('resize', resizeHandler);

    // Reset styles on cleanup using empty string values
    const cleanupStyles = {
      container: {
        position: '',
        overflow: '',
        height: '',
      },
      image: {
        position: '',
        top: '',
        left: '',
        width: '',
        height: '',
        objectFit: '',
        willChange: '',
        transform: '',
        transformOrigin: '',
        transition: '',
      },
    };

    // Apply cleanup styles
    Object.assign(parentContainer.style, cleanupStyles.container);
    Object.assign(collectionImg.style, cleanupStyles.image);
  };
};
